import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Iconography,
  IconographySizes,
  Notification,
  NotificationVariants,
} from "@hid-galaxy-ui/galaxy-react";
import { Dialog, DialogContent } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../../../../redux";
import { partnerProgramBenefitsSliceAction } from "../../../../../../../redux/slices/partner-program/benefits";
import {
  deletePartnerProgramBenefitCategorySagaAction,
  deletePartnerProgramBenefitSagaAction,
  getPartnerProgramBenefitsSagaAction,
  insertPartnerProgramBenefitsSagaAction,
} from "../../../../../../../redux/saga/partner-program/benefits/saga-actions";
import AddBenefitModal from "./components/AddBenefitModal";
import AddCategoryModal from "./components/AddCategoryModal";
import BenefitsCategoryCard from "./components/BenefitsCategoryCard";
import DescriptionModal from "./components/DescriptionModal";
import DeleteBenefitsModal from "./components/DeleteBenefitsModal";
import "./Benefits.scss";

const Benefits: React.FC<{ showQuickActions?: boolean; readOnly?: boolean }> = ({
  showQuickActions = true,
  readOnly = false,
}) => {
  const dispatch = useAppDispatch();
  const [toasts, setToasts] = useState<any[]>([]);
  const [addCategoryModalOpen, setAddCategoryModalOpen] = useState(false);
  const [addBenefitModalState, setAddBenefitModalState] = useState<{
    isOpen: boolean;
    mode: "add" | "edit";
    categoryId: string | null;
    benefitId: string | null;
  }>({
    isOpen: false,
    mode: "add",
    categoryId: null,
    benefitId: null,
  });
  const [descriptionModalState, setDescriptionModalState] = useState<{
    isOpen: boolean;
    categoryId: string | null;
    benefitId: string | null;
  }>({
    isOpen: false,
    categoryId: null,
    benefitId: null,
  });
  const [deleteModalState, setDeleteModalState] = useState<{
    isOpen: boolean;
    type: "category" | "benefit";
    categoryId: string | null;
    benefitId: string | null;
    benefitApiId: number | null;
    itemName: string;
  }>({
    isOpen: false,
    type: "category",
    categoryId: null,
    benefitId: null,
    benefitApiId: null,
    itemName: "",
  });
  const [pendingCategoryIds, setPendingCategoryIds] = useState<string[]>([]);
  const [unsavedCategoryModalOpen, setUnsavedCategoryModalOpen] = useState(false);

  const { categories, tierDetails, isEditing, draftCategories, status } = useAppSelector(
    (state) => state.partner_program_benefits
  );

  useEffect(() => {
    dispatch(partnerProgramBenefitsSliceAction.cancelBenefitsEditMode());
    dispatch(getPartnerProgramBenefitsSagaAction());
  }, [dispatch]);

  useEffect(() => {
    if (readOnly) {
      dispatch(partnerProgramBenefitsSliceAction.cancelBenefitsEditMode());
    }
  }, [readOnly, dispatch]);

  const effectiveEditing = readOnly ? false : isEditing;

  const renderedCategories = useMemo(() => {
    return effectiveEditing ? draftCategories : categories;
  }, [categories, draftCategories, effectiveEditing]);

  const selectedBenefit = useMemo(() => {
    if (!descriptionModalState.categoryId || !descriptionModalState.benefitId) return null;
    const selectedCategory = renderedCategories.find(
      (category) => category.id === descriptionModalState.categoryId
    );
    if (!selectedCategory) return null;
    return selectedCategory.benefits.find(
      (benefit) => benefit.id === descriptionModalState.benefitId
    );
  }, [descriptionModalState.benefitId, descriptionModalState.categoryId, renderedCategories]);

  const editingBenefit = useMemo(() => {
    if (
      addBenefitModalState.mode !== "edit" ||
      !addBenefitModalState.categoryId ||
      !addBenefitModalState.benefitId
    ) {
      return null;
    }
    const selectedCategory = renderedCategories.find(
      (category) => category.id === addBenefitModalState.categoryId
    );
    if (!selectedCategory) return null;
    return selectedCategory.benefits.find(
      (benefit) => benefit.id === addBenefitModalState.benefitId
    );
  }, [
    addBenefitModalState.benefitId,
    addBenefitModalState.categoryId,
    addBenefitModalState.mode,
    renderedCategories,
  ]);

  const handleAddCategory = (title: string) => {
    const categoryId = `category-${Date.now()}`;
    if (!effectiveEditing) {
      dispatch(partnerProgramBenefitsSliceAction.enterBenefitsEditMode());
    }
    dispatch(
      partnerProgramBenefitsSliceAction.addBenefitsCategory({
        title,
        categoryId,
      })
    );
    setPendingCategoryIds((prev) => [...prev, categoryId]);
    setToasts((prev) => [
      ...prev,
      {
        id: Date.now(),
        title: "",
        message: "Category added. Please add at least one benefit to keep it.",
        variant: NotificationVariants.Success,
        icon: <Iconography icon="circleCheck" size={IconographySizes.Medium} />,
      },
    ]);
  };

  const getMappedTierIds = (activeTierIds: number[]) => {
    return activeTierIds.map((tierId) => {
      const matchedTier = tierDetails.find((tier) => tier.ID === tierId);
      return matchedTier ? matchedTier.ID : tierId;
    });
  };

  const buildInsertPayloadFromCategories = (
    list: typeof renderedCategories
  ) => ({
    benefitCategories: list.map((category) => ({
      category_name: category.title,
      benefits: category.benefits.map((benefit, index) => ({
        ...(benefit.apiId !== undefined && benefit.apiId !== null && benefit.apiId > 0
          ? { ID: benefit.apiId }
          : {}),
        benefits_name: benefit.name,
        description: benefit.description || null,
        sort_order: index + 1,
        tier_ids: getMappedTierIds(benefit.activeTierIds),
        benefits_type: benefit.benefitsType || "",
        benefits_status: benefit.benefitsStatus ?? 0,
        delivery_type: benefit.deliveryType || null,
        weightage: benefit.weightage,
        frequency:
          benefit.deliveryType === "Recurring" ? benefit.frequency || null : null,
      })),
    })),
  });

  const showBenefitsToast = (
    message: string,
    variant: typeof NotificationVariants.Success | typeof NotificationVariants.Error
  ) => {
    setToasts((prev) => [
      ...prev,
      {
        id: Date.now(),
        title: "",
        message,
        variant,
        icon: (
          <Iconography
            icon={variant === NotificationVariants.Success ? "circleCheck" : "ban"}
            size={IconographySizes.Medium}
          />
        ),
      },
    ]);
  };

  const handleAddBenefit = (values: {
    name: string;
    description: string;
    benefitsType: string;
    benefitsStatus: number;
    deliveryType: string;
    weightage: number | null;
    frequency: string;
  }) => {
    if (!addBenefitModalState.categoryId) return;
    const categoryId = addBenefitModalState.categoryId;

    if (addBenefitModalState.mode === "edit" && addBenefitModalState.benefitId) {
      dispatch(
        partnerProgramBenefitsSliceAction.updateBenefitInCategory({
          categoryId,
          benefitId: addBenefitModalState.benefitId,
          name: values.name,
          description: values.description,
          benefitsType: values.benefitsType,
          benefitsStatus: values.benefitsStatus,
          deliveryType: values.deliveryType,
          weightage: values.weightage,
          frequency: values.frequency,
        })
      );
      return;
    }

    dispatch(
      partnerProgramBenefitsSliceAction.addBenefitToCategory({
        categoryId,
        name: values.name,
        description: values.description,
        benefitsType: values.benefitsType,
        benefitsStatus: values.benefitsStatus,
        deliveryType: values.deliveryType,
        weightage: values.weightage,
        frequency: values.frequency,
      })
    );
    setPendingCategoryIds((prev) => prev.filter((id) => id !== categoryId));
  };

  const handleSaveBenefits = () => {
    const payloadSource = isEditing ? draftCategories : categories;
    dispatch(
      insertPartnerProgramBenefitsSagaAction({
        ...buildInsertPayloadFromCategories(payloadSource),
        onSuccess: (message) => {
          dispatch(partnerProgramBenefitsSliceAction.saveBenefitsCategoryChanges());
          showBenefitsToast(
            message || "Benefits updated successfully.",
            NotificationVariants.Success
          );
        },
        onError: (message) => {
          showBenefitsToast(
            message || "Unable to save benefits.",
            NotificationVariants.Error
          );
        },
      })
    );
  };

  const handleCancelEdit = () => {
    if (pendingCategoryIds.length > 0) {
      setUnsavedCategoryModalOpen(true);
      return;
    }
    dispatch(partnerProgramBenefitsSliceAction.cancelBenefitsEditMode());
  };

  return (
    <div className="benefits-tab">
      <Notification toasts={toasts} setToasts={setToasts} />
      <div className="benefits-main-panel">
        <div className="benefits-title-row">
          <div>
            <h2>Benefit Category</h2>
            <p>Configure benefits under tiers</p>
          </div>
          <div className="benefits-header-actions">
            {effectiveEditing ? (
              <>
                <Button
                  variant="secondary"
                  label="CANCEL"
                  className="benefits-inline-link"
                  onClick={handleCancelEdit}
                  disabled={readOnly}
                />
                <Button
                  variant="primary"
                  label="SAVE"
                  className="benefits-primary-btn"
                  onClick={handleSaveBenefits}
                  disabled={readOnly}
                />
              </>
            ) : (
              <>
                <Button
                  variant="secondary"
                  label="EDIT"
                  icon={<Iconography icon="pen" />}
                  className="benefits-inline-link"
                  onClick={() =>
                    dispatch(partnerProgramBenefitsSliceAction.enterBenefitsEditMode())
                  }
                  disabled={readOnly}
                />
                <Button
                  variant="primary"
                  label="Add Category"
                  icon={<Iconography icon="plus" />}
                  className="benefits-primary-btn"
                  onClick={() => setAddCategoryModalOpen(true)}
                  disabled={readOnly}
                />
              </>
            )}
          </div>
        </div>

        {status.benefitsLoading ? (
          <div className="benefits-empty-state">Loading benefits...</div>
        ) : renderedCategories.length === 0 ? (
          <div className="benefits-empty-state">No categories found.</div>
        ) : (
          renderedCategories.map((category) => (
            <BenefitsCategoryCard
              key={category.id}
              category={category}
              tierDetails={tierDetails}
              isEditing={effectiveEditing}
              onToggleCollapse={(categoryId) =>
                dispatch(
                  partnerProgramBenefitsSliceAction.toggleBenefitsCategoryCollapse(
                    categoryId
                  )
                )
              }
              onDeleteCategory={(categoryId, categoryName) =>
                setDeleteModalState({
                  isOpen: true,
                  type: "category",
                  categoryId,
                  benefitId: null,
                  benefitApiId: null,
                  itemName: categoryName,
                })
              }
              onUpdateTitle={(categoryId, value) =>
                dispatch(
                  partnerProgramBenefitsSliceAction.updateBenefitsDraftTitle({
                    categoryId,
                    value,
                  })
                )
              }
              onOpenAddBenefit={(categoryId) =>
                setAddBenefitModalState({
                  isOpen: true,
                  mode: "add",
                  categoryId,
                  benefitId: null,
                })
              }
              onOpenEditBenefit={(categoryId, benefitId) =>
                setAddBenefitModalState({
                  isOpen: true,
                  mode: "edit",
                  categoryId,
                  benefitId,
                })
              }
              onOpenDescription={(categoryId, benefitId) =>
                setDescriptionModalState({
                  isOpen: true,
                  categoryId,
                  benefitId,
                })
              }
              onToggleTier={(categoryId, benefitId, tier) =>
                dispatch(
                  partnerProgramBenefitsSliceAction.toggleBenefitsDraftTierFlag({
                    categoryId,
                    benefitId,
                    tierId: tier,
                  })
                )
              }
              onDeleteBenefit={(categoryId, benefitId, benefitName, benefitApiId) =>
                setDeleteModalState({
                  isOpen: true,
                  type: "benefit",
                  categoryId,
                  benefitId,
                  benefitApiId: benefitApiId ?? null,
                  itemName: benefitName,
                })
              }
              onReorder={(categoryId, fromIndex, toIndex) =>
                dispatch(
                  partnerProgramBenefitsSliceAction.reorderBenefitsDraftRows({
                    categoryId,
                    fromIndex,
                    toIndex,
                  })
                )
              }
            />
          ))
        )}
      </div>

      {showQuickActions && null}

      <AddCategoryModal
        open={addCategoryModalOpen}
        onClose={() => setAddCategoryModalOpen(false)}
        onSubmit={handleAddCategory}
      />

      <AddBenefitModal
        open={addBenefitModalState.isOpen}
        mode={addBenefitModalState.mode}
        initialValues={
          editingBenefit
            ? {
                name: editingBenefit.name,
                description:
                  editingBenefit.description?.trim() === "View"
                    ? ""
                    : editingBenefit.description,
                benefitsType: editingBenefit.benefitsType,
                benefitsStatus: editingBenefit.benefitsStatus,
                deliveryType: editingBenefit.deliveryType,
                weightage: editingBenefit.weightage,
                frequency: editingBenefit.frequency,
              }
            : null
        }
        onClose={() =>
          setAddBenefitModalState({
            isOpen: false,
            mode: "add",
            categoryId: null,
            benefitId: null,
          })
        }
        onSubmit={handleAddBenefit}
      />

      <DescriptionModal
        open={descriptionModalState.isOpen}
        editable={false}
        title={selectedBenefit?.name || "Benefit Description"}
        value={
          selectedBenefit?.description?.trim() === "View"
            ? ""
            : selectedBenefit?.description || ""
        }
        onClose={() =>
          setDescriptionModalState({
            isOpen: false,
            categoryId: null,
            benefitId: null,
          })
        }
        onSave={() => undefined}
      />

      <DeleteBenefitsModal
        open={deleteModalState.isOpen}
        type={deleteModalState.type}
        itemName={deleteModalState.itemName}
        onClose={() =>
          setDeleteModalState({
            isOpen: false,
            type: "category",
            categoryId: null,
            benefitId: null,
            benefitApiId: null,
            itemName: "",
          })
        }
        onConfirm={() => {
          if (deleteModalState.type === "category" && deleteModalState.categoryId) {
            if (pendingCategoryIds.includes(deleteModalState.categoryId)) {
              dispatch(
                partnerProgramBenefitsSliceAction.deleteBenefitsCategory(
                  deleteModalState.categoryId
                )
              );
              setPendingCategoryIds((prev) =>
                prev.filter((id) => id !== deleteModalState.categoryId)
              );
              dispatch(partnerProgramBenefitsSliceAction.cancelBenefitsEditMode());
            } else {
              const deletingCategoryId = deleteModalState.categoryId;
              dispatch(
                deletePartnerProgramBenefitCategorySagaAction({
                  category_name: deleteModalState.itemName,
                  onSuccess: (message) => {
                    dispatch(
                      partnerProgramBenefitsSliceAction.deleteBenefitsCategory(
                        deletingCategoryId
                      )
                    );
                    dispatch(partnerProgramBenefitsSliceAction.cancelBenefitsEditMode());
                    setToasts((prev) => [
                      ...prev,
                      {
                        id: Date.now(),
                        title: "",
                        message: message || "Category deleted successfully.",
                        variant: NotificationVariants.Success,
                        icon: (
                          <Iconography
                            icon="circleCheck"
                            size={IconographySizes.Medium}
                          />
                        ),
                      },
                    ]);
                  },
                  onError: (message) => {
                    setToasts((prev) => [
                      ...prev,
                      {
                        id: Date.now(),
                        title: "",
                        message: message || "Unable to delete category.",
                        variant: NotificationVariants.Error,
                        icon: <Iconography icon="ban" size={IconographySizes.Medium} />,
                      },
                    ]);
                  },
                })
              );
            }
          }

          if (
            deleteModalState.type === "benefit" &&
            deleteModalState.categoryId &&
            deleteModalState.benefitId
          ) {
            if (deleteModalState.benefitApiId && deleteModalState.benefitApiId > 0) {
              const deletingCategoryId = deleteModalState.categoryId;
              const deletingBenefitId = deleteModalState.benefitId;
              dispatch(
                deletePartnerProgramBenefitSagaAction({
                  ID: deleteModalState.benefitApiId,
                  onSuccess: (message) => {
                    dispatch(
                      partnerProgramBenefitsSliceAction.deleteBenefitsDraftRow({
                        categoryId: deletingCategoryId,
                        benefitId: deletingBenefitId,
                      })
                    );
                    dispatch(partnerProgramBenefitsSliceAction.cancelBenefitsEditMode());
                    setToasts((prev) => [
                      ...prev,
                      {
                        id: Date.now(),
                        title: "",
                        message: message || "Benefit deleted successfully.",
                        variant: NotificationVariants.Success,
                        icon: (
                          <Iconography
                            icon="circleCheck"
                            size={IconographySizes.Medium}
                          />
                        ),
                      },
                    ]);
                  },
                  onError: (message) => {
                    setToasts((prev) => [
                      ...prev,
                      {
                        id: Date.now(),
                        title: "",
                        message: message || "Unable to delete benefit.",
                        variant: NotificationVariants.Error,
                        icon: <Iconography icon="ban" size={IconographySizes.Medium} />,
                      },
                    ]);
                  },
                })
              );
            } else {
              dispatch(
                partnerProgramBenefitsSliceAction.deleteBenefitsDraftRow({
                  categoryId: deleteModalState.categoryId,
                  benefitId: deleteModalState.benefitId,
                })
              );
              dispatch(partnerProgramBenefitsSliceAction.cancelBenefitsEditMode());
            }
          }

          setDeleteModalState({
            isOpen: false,
            type: "category",
            categoryId: null,
            benefitId: null,
            benefitApiId: null,
            itemName: "",
          });
        }}
      />

      <Dialog
        open={unsavedCategoryModalOpen}
        onClose={() => setUnsavedCategoryModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <div className="benefits-ptv-modal benefits-delete-modal">
          <header className="benefits-ptv-modal-header">
            <div className="benefits-delete-heading">
              <span className="benefits-delete-icon">
                <Iconography icon="circleExclamation" size={IconographySizes.Small} />
              </span>
              <h3>Unsaved category found</h3>
            </div>
          </header>
          <DialogContent className="benefits-ptv-modal-content">
            <p>
              You have to add at least one benefit. Added category will be removed if
              you continue.
            </p>
          </DialogContent>
          <footer className="benefits-ptv-modal-footer">
            <Button
              variant="secondary"
              label="CANCEL"
              onClick={() => setUnsavedCategoryModalOpen(false)}
            />
            <Button
              variant="primary"
              label="CONTINUE"
              onClick={() => {
                dispatch(
                  partnerProgramBenefitsSliceAction.removeBenefitsCategoriesByIds(
                    pendingCategoryIds
                  )
                );
                setPendingCategoryIds([]);
                setUnsavedCategoryModalOpen(false);
                dispatch(partnerProgramBenefitsSliceAction.cancelBenefitsEditMode());
              }}
            />
          </footer>
        </div>
      </Dialog>
    </div>
  );
};

export default Benefits;
